type FactoryWithDeps<TMap, Deps extends (keyof TMap)[], T extends keyof TMap> = {
    deps: [...Deps];
    create: (...args: { [I in keyof Deps]: TMap[Deps[I]] }) => TMap[T];
};

function isConstructor(func: any): func is new (...args: any[]) => any {
    return typeof func === "function" && /^class\s/.test(Function.prototype.toString.call(func));
}

export class ServiceProvider<TMap extends Record<string, any>> {
    protected services: Partial<TMap> = {};
    protected serviceFactories: Partial<
        Record<keyof TMap, {
            deps: (keyof TMap)[];
            create: (...args: any[]) => TMap[keyof TMap];
        }>
    > = {};
    protected built = false;

    public RegisterFactory<
        TService extends keyof TMap,
        Deps extends (keyof TMap)[],
        Ctor extends new (...args: { [I in keyof Deps]: TMap[Deps[I]] }) => TMap[TService]
    >(
        name: TService,
        deps: [...Deps],
        factory: Ctor | ((...args: { [I in keyof Deps]: TMap[Deps[I]] }) => TMap[TService])
    ): void {
        if (this.built) throw new Error("Services already built");
        if (this.services[name] != null) throw new Error(`Service ${String(name)} already registered`);
        if (this.serviceFactories[name] != null) throw new Error(`Service factory ${String(name)} already registered`);

        const wrappedFactory = (...args: { [I in keyof Deps]: TMap[Deps[I]] }) => {
            return isConstructor(factory)
                ? new factory(...args)
                : factory(...args);
        };

        this.serviceFactories[name] = {
            deps,
            create: wrappedFactory
        };
    }

    public RegisterValue<T extends keyof TMap>(name: T, service: TMap[T]): void {
        if (this.built) throw new Error("Services already built");
        if (this.services[name] != null) throw new Error(`Service ${String(name)} already registered`);
        if (this.serviceFactories[name] != null) throw new Error(`Service factory ${String(name)} already registered`);

        this.services[name] = service;
    }

    public Build(): void {
        if (this.built) throw new Error("Services already built");

        const visited = new Set<keyof TMap>();
        const visiting = new Set<keyof TMap>();

        const visit = (key: keyof TMap) => {
            if (this.services[key] != null) return;
            if (visited.has(key)) return;
            if (visiting.has(key)) throw new Error(`Circular dependency detected at: ${String(key)}`);

            const factoryEntry = this.serviceFactories[key];
            if (!factoryEntry) throw new Error(`No factory registered for: ${String(key)}`);

            visiting.add(key);
            const deps = factoryEntry.deps.map(dep => {
                visit(dep);
                return this.InternalResolve(dep);
            });
            visiting.delete(key);

            this.services[key] = factoryEntry.create(...deps);
            visited.add(key);
        };

        for (const key in this.serviceFactories) {
            visit(key as keyof TMap);
        }

        this.built = true;
    }

    protected InternalResolve<T extends keyof TMap>(name: T): TMap[T] {
        const service = this.services[name];
        if (service == null) throw new Error(`Service ${String(name)} not found or constructed`);

        return service as TMap[T];
    }

    public Resolve<T extends keyof TMap>(name: T): TMap[T] {
        if (!this.built) throw new Error("Services not built");

        const service = this.InternalResolve(name);
        return service;
    }
}
